import {toBase64, isDefined} from '@stubhub/general-utils';
import {gcpTree, getGeoId, getGeoTree, getParentGCPId} from '@stubhub/tracking-utils';

import {SELL_FLOW_START} from './track-enum';

const getUserGUID = (cookies) => (cookies && cookies.get && cookies.get('session_userGUID')) || null;

const getUniqueArray = (array, field) => {
  return Array.from(new Set(array?.map((item) => item[field]?.toLowerCase())));
};

const getTree = (elements = []) => {
  const tree = elements.filter((item) => !!item).join('/');
  if (tree.length) {
    return `/${tree}/`;
  }

  return '/';
};

const screenView = (track, {screenName, storeId, storeCountry, locale, userGUID} = {}) => {
  track.screen_view({
    screen_name: screenName,
    store_id: storeId?.toString() || null,
    store_country: storeCountry || null,
    visit_platform: 'sh-web',
    visit_locale: locale || null,
    visit_guid: userGUID || null,
  });
};

const use_feature = (track, {storeId, storeCountry, locale, userGUID, feature_name, feature_details} = {}) => {
  track.custom_event({
    event: 'use_feature',
    feature_name,
    feature_details,
    store_id: storeId?.toString() || null,
    store_country: storeCountry || null,
    visit_platform: 'sh-web',
    visit_locale: locale || null,
    visit_guid: userGUID || null,
  });
};

const track_update_listing = (track, {field, change, listingIds, storeId, storeCountry, locale, userGUID} = {}) => {
  track.custom_event({
    event: 'update_listing',
    details: `${field};${change}`,
    item_list_qty: listingIds.length,
    items: listingIds.map(({listingId, eventId}) => ({
      item_name: `sh-listing-${listingId}`,
      item_id: listingId,
      ...(eventId
        ? {
            event_id_unique: `sh-event-${eventId}`,
            event_id: eventId,
          }
        : {}),
    })),
  });
};

const delete_listings = (track, {storeId, storeCountry, locale, userGUID, listingIds} = {}) => {
  track.custom_event({
    event: 'delete_listing',
    store_id: storeId?.toString() || null,
    store_country: storeCountry || null,
    visit_platform: 'sh-web',
    visit_locale: locale || null,
    visit_guid: userGUID || null,
    item_list_qty: listingIds?.length || 0,
    items: listingIds.map(({listingId, eventId}) => ({
      item_name: `sh-listing-${listingId}`,
      item_id: listingId,
      event_id_unique: `sh-listing-${eventId}`,
      event_id: eventId,
    })),
  });
};

const error_platform = (track, {storeId, storeCountry, locale, userGUID, feature_name, feature_details} = {}) => {
  track.custom_event({
    event: 'error_platform',
    feature_name,
    feature_details,
    store_id: storeId?.toString() || null,
    store_country: storeCountry || null,
    visit_platform: 'sh-web',
    visit_locale: locale || null,
    visit_guid: userGUID || null,
  });
};

/**
 *
 * Sends a begin_publish track with this data:
 *
 * dataLayer.push({
 *  “event”: “begin_publish”, // a custom GA4 event
 *  "sell_flow_start": "", // one of "hub", "event", "relist", "other”
 *  “ticket_mediums_available_qty”: N, // the amount of ticket mediums offered
 *  “ticket_mediums_available”: ”/XXX/YYY/ZZZ/”, // concatenated names as a string
 *  “fulfillment_methods_available_qty”: N, // the amount of fulfillment methods offered
 *  “fulfillment_methods_available”: ”/XXX/YYY/ZZZ/”, // concatenated names as a string
 *});
 *
 * @param {object} track the track object with at least custom method
 * @param {object} data the data sent to track
 */
const beginPublish = (
  track,
  {
    eventId,
    eventName,
    daysToEvent,
    genre,
    inventoryMarket,
    ancestors = {},
    countryCode,
    fulfillmentMethods = [],
    sellFlowStart,
    onSaleDate,
    imageURL,
    eventDateUTC,
  }
) => {
  const ticketMediums = getUniqueArray(fulfillmentMethods, 'ticketMedium');
  const fulfillmentNames = getUniqueArray(fulfillmentMethods, 'name');

  track.custom_event({
    event: 'begin_publish',
    sell_flow_start: sellFlowStart || SELL_FLOW_START.OTHER,
    ticket_mediums_available_qty: ticketMediums.length || 0,
    ticket_mediums_available: getTree(ticketMediums),
    fulfillment_methods_available_qty: fulfillmentNames.length || 0,
    fulfillment_methods_available: getTree(fulfillmentNames),
    event_id: eventId?.toString() || null,
    event_name: eventName ? toBase64(eventName) : null,
    days_to_event: isDefined(daysToEvent) ? daysToEvent : null,
    genre: genre || null,
    inventory_market: inventoryMarket || null,
    parent_gcp_id: (ancestors && getParentGCPId(ancestors)?.toString()) || null,
    gcp_tree: gcpTree(ancestors),
    country_code: countryCode || null,
    geo_id: (ancestors && getGeoId(ancestors)?.toString()) || null,
    geo_tree: getGeoTree(ancestors || {}),
    picture_url: imageURL,
    event_date_ts: eventDateUTC ? new Date(eventDateUTC).getTime() / 1000 : null,
    event_onsale_ts: onSaleDate ? new Date(onSaleDate).getTime() / 1000 : null,
  });
};

const successPublish = (
  track,
  {
    eventId,
    eventName,
    daysToEvent,
    genre,
    inventoryMarket,
    ancestors = {},
    eventCountry,
    fulfillmentMethods = [],
    medium,
    fulFillmentMethod,
    deliveryNow,
    numTickets,
    connections,
    faceValue,
    ticketPrice,
    ticketCurrency,
    numFeatures,
    numRestrictions,
    sellerType,
    paymentMethod,
    sellFlowStart,
    listingId,
    onSaleDate,
    imageURL,
    eventDateUTC,
  }
) => {
  const ticketMediums = getUniqueArray(fulfillmentMethods, 'ticketMedium');
  const fulfillmentNames = getUniqueArray(fulfillmentMethods, 'name');

  track.custom_event({
    event: 'publish',
    // Flow info
    sell_flow_start: sellFlowStart, // one of "hub", "event", "relist", "other'

    // Form info
    ticket_mediums_available_qty: ticketMediums.length || 0,
    ticket_mediums_available: getTree(ticketMediums),
    fulfillment_methods_available_qty: fulfillmentNames.length || 0,
    fulfillment_methods_available: getTree(fulfillmentNames),

    // Event info
    event_id: eventId?.toString() || null,
    event_name: eventName ? toBase64(eventName) : null,
    days_to_event: isDefined(daysToEvent) ? daysToEvent : null,
    genre: genre || null,
    inventory_market: inventoryMarket || null,
    parent_gcp_id: (ancestors && getParentGCPId(ancestors)?.toString()) || null,
    gcp_tree: gcpTree(ancestors),
    country_code: eventCountry || null,
    geo_id: (ancestors && getGeoId(ancestors)?.toString()) || null,
    geo_tree: getGeoTree(ancestors || {}),
    picture_url: imageURL,
    event_date_ts: eventDateUTC ? new Date(eventDateUTC).getTime() / 1000 : null,
    event_onsale_ts: onSaleDate ? new Date(onSaleDate).getTime() / 1000 : null,

    // Listing info
    ticket_medium: medium, // one of 'paper', 'pdf', etc.
    fulfillment: fulFillmentMethod, // one of 'digital', 'shipping', etc.
    tickets_in_hand: deliveryNow, // one of 'ready', 'later'
    ticket_qty: numTickets, // the amount of tickets in the listing
    face_value: faceValue, // in the listing’s own currency, the average if multiple, null if not provided
    price: ticketPrice, // in the listing’s own currency
    currency: ticketCurrency, // the original one for the listing, e.g. 'EUR', 'USD'
    features_qty: numFeatures, // the amount of features & comments (defaults to 0)
    restrictions_qty: numRestrictions, // the amount of restrictions & limitations (defaults to 0)
    connected: connections, // one of 'no' (default), 'employee', 'company', 'organizer'
    listing_id: listingId?.toString() || null, // the listing ID that has been created

    // Seller info
    seller_type: sellerType, //one of 'PRIVATE', 'BUSINESS'
    payout_method: paymentMethod, // one of 'paypal', 'direct_deposit'
    guarantee_cc: null, // one of 'existing', 'new'
  });
};

export {
  screenView,
  getUserGUID,
  use_feature,
  error_platform,
  track_update_listing,
  delete_listings,
  beginPublish,
  successPublish,
};
